import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useData } from '../DataContext';
import { X, User, Phone, CreditCard,  Calendar } from 'lucide-react';

export default function CustomersPage() {
  const { customers, setCustomers } = useData();
  const [showModal, setShowModal] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [depositInput, setDepositInput] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  // 👁️ Просмотр клиента
  function handleViewCustomer(customer) {
    setViewingCustomer(customer);
    setIsEditing(false);
    setDepositInput(formatMoney(customer.deposit));
    setShowModal(true);
  }

  // ✅ Добавление / редактирование
  async function handleAddOrEditCustomer(e) {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const form = e.target;
      const cleanDeposit = depositInput.replace(/\s/g, '').replace(',', '.');
      const data = {
        fullName: form.fullName.value.trim(),
        phone: form.phone.value.trim(),
        passport: form.passport.value.trim(),
        deposit: parseFloat(cleanDeposit) || 0,
      };

      if (viewingCustomer && isEditing) {
        const res = await axios.put(`${API_URL}/api/customers/${viewingCustomer._id}`, data, headers);
        setCustomers((prev) =>
          prev.map((c) => (c._id === viewingCustomer._id ? res.data : c))
        );
      } else {
        const res = await axios.post(`${API_URL}/api/customers`, data, headers);
        setCustomers((prev) => [...prev, res.data]);
      }

      setShowModal(false);
      setViewingCustomer(null);
      setIsEditing(false);
      setDepositInput('');
    } catch (error) {
      console.error('Ошибка сохранения клиента:', error);
      alert('Ошибка при сохранении клиента');
    } finally {
      setSubmitting(false);
    }
  }

  // 🗑️ Удаление
  async function handleDeleteCustomer() {
    if (!viewingCustomer) return;
    if (!window.confirm(`Удалить клиента "${viewingCustomer.fullName}"?`)) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/customers/${viewingCustomer._id}`, headers);
      setCustomers((prev) => prev.filter((c) => c._id !== viewingCustomer._id));
      setShowModal(false);
      setViewingCustomer(null);
      setIsEditing(false);
    } catch (error) {
      console.error('Ошибка удаления клиента:', error);
      alert('Ошибка при удалении клиента');
    } finally {
      setSubmitting(false);
    }
  }

  const formatMoney = (value) =>
    new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2 }).format(value || 0);

  // 🔍 Фильтр по поиску
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return customers.filter(
      (c) =>
        c.fullName.toLowerCase().includes(q) ||
        c.phone.toLowerCase().includes(q) ||
        c.passport.toLowerCase().includes(q)
    );
  }, [customers, search]);

  return (
    <div className="p-2 sm:p-4 space-y-4">
      {/* 🔹 Заголовок и кнопка */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold text-zinc-100">Клиенты</h1>
          <p className="text-zinc-400 text-sm mt-1">Всего клиентов: {customers.length}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 rounded bg-zinc-800 text-zinc-100 placeholder-zinc-500 w-full sm:w-64 focus:outline-none"
          />
          <button
            onClick={() => {
              setViewingCustomer(null);
              setIsEditing(false);
              setDepositInput('');
              setShowModal(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-sm sm:text-base px-4 py-2 rounded w-full sm:w-auto transition-colors"
          >
            Добавить клиента
          </button>
        </div>
      </div>

      {/* 💻 Таблица (для десктопа) */}
      <div className="hidden md:block bg-zinc-900 rounded-lg overflow-x-auto shadow-md">
        <table className="min-w-full text-left text-sm sm:text-base">
          <thead className="text-zinc-400 border-b border-zinc-800">
            <tr>
              <th className="p-3">Имя</th>
              <th className="p-3">Телефон</th>
              <th className="p-3">Паспорт</th>
              <th className="p-3">Депозит</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr 
                key={c._id} 
                className="border-t border-zinc-800 hover:bg-zinc-800/40 cursor-pointer transition-colors"
                onClick={() => handleViewCustomer(c)}
              >
                <td className="p-3 font-medium">{c.fullName}</td>
                <td className="p-3">{c.phone}</td>
                <td className="p-3">{c.passport}</td>
                <td className="p-3 whitespace-nowrap text-emerald-400 font-semibold">
                  {formatMoney(c.deposit)} сум
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 📱 Карточки (для мобилки) */}
      <div className="grid grid-cols-1 gap-3 md:hidden">
        {filtered.map((c) => (
          <div
            key={c._id}
            onClick={() => handleViewCustomer(c)}
            className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 shadow-md hover:border-zinc-700 transition-all cursor-pointer"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="h-12 w-12 bg-emerald-600/20 border border-emerald-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-400 font-bold text-lg">
                  {c.fullName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="text-white text-base font-medium">{c.fullName}</div>
                <div className="text-zinc-500 text-xs">{c.phone}</div>
              </div>
            </div>
            
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2 text-zinc-400">
                <CreditCard className="w-4 h-4" />
                <span>{c.passport}</span>
              </div>
              <div className="flex items-center gap-2">
                
                <span className="text-emerald-400 font-semibold">{formatMoney(c.deposit)} сум</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Пустое состояние */}
      {filtered.length === 0 && (
        <div className="text-center py-12 bg-zinc-900 rounded-xl border border-zinc-800">
          <h3 className="text-xl font-semibold text-zinc-100 mb-2">
            {search ? 'Клиенты не найдены' : 'Нет клиентов'}
          </h3>
          <p className="text-zinc-400 mb-6">
            {search ? 'Попробуйте изменить параметры поиска' : 'Добавьте первого клиента'}
          </p>
          {!search && (
            <button
              onClick={() => {
                setViewingCustomer(null);
                setIsEditing(false);
                setDepositInput('');
                setShowModal(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 px-6 py-2 rounded transition-colors"
            >
              Добавить клиента
            </button>
          )}
        </div>
      )}

      {/* 🔹 Модалка */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-5 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-zinc-100">
                  {viewingCustomer && !isEditing 
                    ? 'Информация о клиенте' 
                    : viewingCustomer 
                    ? 'Редактировать клиента' 
                    : 'Добавить клиента'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setViewingCustomer(null);
                    setIsEditing(false);
                  }}
                  className="text-zinc-400 hover:text-zinc-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {viewingCustomer && !isEditing ? (
                /* 👁️ Режим просмотра */
                <div className="space-y-4">
                  <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-16 w-16 bg-emerald-600/20 border border-emerald-500/30 rounded-full flex items-center justify-center">
                        <span className="text-emerald-400 font-bold text-2xl">
                          {viewingCustomer.fullName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">{viewingCustomer.fullName}</h3>
                        <p className="text-zinc-400 text-sm">Клиент</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-3 border-t border-zinc-700">
                      <Phone className="w-4 h-4 text-zinc-400" />
                      <span className="text-white">{viewingCustomer.phone}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-zinc-400" />
                      <span className="text-white">{viewingCustomer.passport}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      
                      <span className="text-emerald-400 font-bold text-lg">
                        {formatMoney(viewingCustomer.deposit)} сум
                      </span>
                    </div>

                    {viewingCustomer.createdAt && (
                      <div className="flex items-center gap-2 pt-2">
                        <Calendar className="w-4 h-4 text-zinc-400" />
                        <span className="text-zinc-400 text-sm">
                          Добавлен: {new Date(viewingCustomer.createdAt).toLocaleDateString('ru-RU')}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteCustomer}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting ? 'Удаление...' : 'Удалить'}
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                      disabled={submitting}
                    >
                      Изменить
                    </button>
                  </div>
                </div>
              ) : (
                /* ✏️ Режим редактирования / создания */
                <form onSubmit={handleAddOrEditCustomer} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Имя *
                    </label>
                    <input
                      name="fullName"
                      placeholder="Введите имя"
                      defaultValue={viewingCustomer?.fullName || ''}
                      className="w-full p-3 rounded-lg bg-zinc-800 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Телефон *
                    </label>
                    <input
                      name="phone"
                      placeholder="+998 90 123 45 67"
                      defaultValue={viewingCustomer?.phone || ''}
                      className="w-full p-3 rounded-lg bg-zinc-800 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Паспорт *
                    </label>
                    <input
                      name="passport"
                      placeholder="AA 1234567"
                      defaultValue={viewingCustomer?.passport || ''}
                      className="w-full p-3 rounded-lg bg-zinc-800 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Депозит (сум) *
                    </label>
                    <input
                      name="deposit"
                      type="text"
                      placeholder="0.00"
                      value={depositInput}
                      onChange={(e) => setDepositInput(e.target.value.replace(/[^\d.,]/g, ''))}
                      onBlur={() => {
                        const clean = depositInput.replace(/\s/g, '').replace(',', '.');
                        if (!isNaN(clean) && clean !== '') {
                          setDepositInput(formatMoney(parseFloat(clean)));
                        }
                      }}
                      className="w-full p-3 rounded-lg bg-zinc-800 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      required
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (viewingCustomer) {
                          setIsEditing(false);
                        } else {
                          setShowModal(false);
                        }
                      }}
                      className="flex-1 bg-zinc-700 hover:bg-zinc-600 px-4 py-3 rounded-lg text-sm transition-colors"
                      disabled={submitting}
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 px-4 py-3 rounded-lg text-sm transition-colors disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting ? 'Сохранение...' : (viewingCustomer ? 'Сохранить' : 'Создать')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}